import { sheetColumnsOf } from "./sheet-columns-of.ts";
import { $$ } from "@tento-ui/fastdom";
import type { BindSheetOptions, SheetElement } from "./sheet-element.ts";
import type { EmitHandle } from "./sheet-emit-handle.ts";
import { ColumnTimepointSearch } from "@tento-lona/platform";
import { type Option, RowEnvelopes } from "@tento-lona/sheets";
import { AliasRow } from "@tento-lona/sheets/platform";
import { RowLocalId } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { DateTime, GenericRange, MsSinceEpoch, Time, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetLayout } from "@tento-ui/components/sheet/sheet-layout";
import { Toast } from "@tento-ui/components/toast/toast";
import { DomUtils } from "@tento-ui/dom";
import { FrameMarks } from "@tento-ui/frame-marks";
import { Utils } from "@tento-ui/utils";
import { BindReasonLedger } from "./bind-reasons.ts";
import { rowContentIntersects, type RowContentRanges } from "@tento-lona/sheets/internal-ui-frame";
import { CalendarReadRevision } from "./calendar-read-revision.ts";
import { DefaultBinderHelpers } from "./binder/default-binder-helpers.ts";
import { RenderPluginManager, RowOptionsElement } from "./render-plugin.ts";
import { RowOptionsAnchor } from "./row-options-anchor.ts";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import { SheetLegend } from "./sheet-legend.ts";
import type { SheetNavigator } from "@tento-ui/components/sheet/sheet-navigator";
import type { Size } from "@tento-ui/layout-utils";
import { liveVariantOf } from "./live-variant.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import type { SheetElementCore } from "./sheet-element-core.ts";

/**
 * The sheet element's rebind handle (F11): every column/row/timepoint/all
 * rebind entry point, structural rebinds, legend and row-options rebinds.
 * Moved out of sheet-element.ts; the element is read through the host when a
 * method runs, and the handle's own state (`RebindHandleState`) is written
 * here only.
 */

/**
 * The row-options surface and the structural-rebind coalescer. Owner: this
 * module; the element reads the anchor (scroll and geometry
 * rebinds re-place it).
 */
export class RebindHandleState {
  /** The open row-options element. */
  currentRowOptions: Option<RowOptionsElement> = null;
  /** The row-options anchor. */
  rowOptionsAnchor: Option<RowOptionsAnchor> = null;
  /** The structural rebind in flight. */
  structuralRebindInFlight: Option<Promise<void>> = null;
  /** Whether another structural rebind is queued. */
  structuralRebindQueued = false;
}

/** What the handle reads on its sheet element (when a method runs), and the state it writes. */
export type RebindHandleHost = {
  /** The element as every step reads it (its bound sheet and layout). */
  readonly core: SheetElementCore;
  /** The row-options and coalescer state; written here only. */
  readonly state: RebindHandleState;
  /** The layout element. */
  readonly root: SheetLayout;
  /** The body legend. */
  readonly legend: SheetLegend;
  /** The header legend. */
  readonly headerLegend: SheetLegend;
  /** The row whose options are open. */
  readonly activeRowOptions: LiveData<Option<RowLocalId>>;
  /** Why each column's next full bind runs (F10). */
  readonly bindReasons: BindReasonLedger;
  /** The element's sheet bind. */
  readonly bindSheet: (sheet: SheetViewModel.Surface<SheetBinder>, options: BindSheetOptions) => Promise<void>;
  /** The composed row-options renderer. */
  readonly composedOptionsRenderer: Option<RowOptionsElement.ComposedRenderer>;
  /** The sheet's clock. */
  readonly currentTime: LiveData<DateTime<Utc>>;
  /** The element's default binder. */
  readonly defaultBinder: SheetBinder;
  /** The element's emit handle. */
  readonly emitHandle: EmitHandle;
  /** The render plugins. */
  readonly pluginManager: Option<RenderPluginManager>;
  /** The structural rebind pass. */
  readonly rebindStructurePass: () => Promise<void>;
  /** Whether the debug popover shows. */
  readonly shouldShowDebugPopover: Option<LiveData.Readable<boolean>>;
  /** Defer a pass during a view-model swap. */
  readonly suppressForViewModelSwap: (pass: string) => boolean;
  /** Whether column rebinds toast. */
  readonly columnRebindToast: Option<LiveData.Readable<boolean>>;
  /** The element's delegate handle. */
  readonly handle: SheetDelegate;
  /** The legend handle. */
  readonly legendHandle: SheetElement.LegendHandle;
  /** The navigator, if any. */
  readonly navigator: Option<SheetNavigator>;
  /** Rebind cell heights. */
  readonly rebindCellHeights: () => void;
  /** Rebind static child positions. */
  readonly rebindStaticChildPositions: () => void;
  /** Rebind fixed child y positions. */
  readonly rebindFixedChildYPositions: () => void;
  /** The measured size. */
  readonly size: Option<Size>;
};

/** The handle the element keeps as `rebindHandle`. */
export function makeRebindHandle(host: RebindHandleHost): SheetDelegate.RebindHandle {
  const handle = {
      get sheet(): SheetViewModel.Surface<SheetBinder> {
        return nonnull(host.core.sheet, "Sheet: no sheet bound");
      },
      rebindViewports() {
        host.emitHandle.rendered();
        host.emitHandle.visible();
      },
      rebindRow(row: SheetRow.Joined.Handle) {
        this.rebindRows([row], null);
      },
      rebindRows(rows: Iterable<SheetRow.Joined.Handle>, ranges: RowContentRanges) {
        if (host.core.sheet) CalendarReadRevision.for(host.core.sheet.invalidation).advance();
        this.rebindInvalidatedRows(rows, ranges);
      },
      rebindInvalidatedRows(rows: Iterable<SheetRow.Joined.Handle>, ranges: RowContentRanges) {
        const sheet = host.core.sheet;
        if (!sheet) return;

        const visibleColumns = host.core.layout.columns.visibleColumns();
        if (!visibleColumns) return;

        const renderedColumns = host.core.layout.columns.renderedColumns();
        if (!renderedColumns) return;

        const renderer = sheet.indexRenderer;
        const visibleDefault = visibleColumns.resolveToDefault(renderer.mapper);
        const renderedDefault = renderedColumns.resolveToDefault(renderer.mapper);

        // Update span-stacked fixed children first. These are absolutely
        // positioned CellGroups outside the scroll engine's rendered pool,
        // so the binder loop below cannot reach them. When async cell data
        // arrives (e.g., TasksIndex populated after fetch), this is the
        // only path that re-binds their plugin cells with fresh data.
        host.defaultBinder.updateStaticChildSpans?.(sheet, visibleDefault);

        const renderedElements = host.core.layout.columns.renderedElements();
        const resolvedElements = renderedElements.map((el) => {
          const resolved = renderer.mapper.resolveKey(el.column);
          const binder =
            renderer.toBinder(resolved) ??
            host.defaultBinder;
          return { el, resolved, binder };
        }).filter(({ resolved }) => {
          if (ranges == null) return true;
          const column = resolved.castToDefault();
          return rowContentIntersects(column == null ? null : {
            local: { start: column.pd.start.dse, end: column.pd.end.dse },
            utc: {
              start: column.range.start.mse / Time.MS_PER_DAY,
              end: column.range.end.mse / Time.MS_PER_DAY,
            },
          }, ranges);
        });

        for (const row of rows) {
          const rowIdx = sheet.joinedTree.peekFlattenedRowIdx(row);
          if (rowIdx == null) continue;

          for (const { el, resolved, binder } of resolvedElements) {
            binder.rebind(
              sheet,
              resolved,
              sheetColumnsOf(el),
              el.widthPx,
              rowIdx,
              host.currentTime,
            );
          }

          // A row with no cell-bindable descendant (an alias whose template
          // expanded no child) has no cell plugin to tell: the binder already
          // treats it as height-only. Skip it and go on with the batch; a
          // throw here left every later row of the batch unpainted.
          const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
            row,
            nonnull(host.pluginManager, "Sheet: pluginManager not set"),
          );
          if (binding == null) continue;
          const plugin = binding.plugin;
          plugin.onBindRow(host.handle, row);
          plugin.onViewportColumnsChanged(
            row,
            visibleDefault,
            host.handle,
            host.headerLegend.$children.get(row.id)?.$content ??
              host.legend.$children.get(row.id)?.$content ?? null,
            "bind",
          );
          plugin.onRenderedColumnsWillChange(row, renderedDefault, host.handle);
        }
      },
      invalidateRowHeight() {
        if (!host.core.sheet) return;
        host.core.sheet.invalidateLayout();
        host.core.layout.bindScrollProps({
          headerHeight: SheetElementLayout.headerHeight(host.core.sheet),
          scrollContentHeight: host.core.sheet.layout.bodyHeightPx,
          footerHeight: host.core.sheet.layout.footerRowsHeightPx || null,
          canShowDebugPopover: host.shouldShowDebugPopover,
        });
        host.core.layout.presentation.setBodyMarginTop(
          SheetElementLayout.bodyMarginTop(host.core.sheet),
        );
        host.core.layout.presentation.setFooterHeight(
          host.core.sheet.layout.footerRowsHeightPx + 2,
        );
        host.rebindCellHeights();
        host.legendHandle.rebindHeights(host.core.sheet);
        host.rebindStaticChildPositions();
        host.core.layout.rebindFixedChildPositions();
        host.rebindFixedChildYPositions();
      },
      async rebindRowLegend(_row: SheetRow.Joined.Handle) {
        // TODO!!: rebind specific legend
        if (!host.core.sheet) return;
        await host.bindSheet(host.core.sheet, {
          animate: false,
          rebindLegend: true,
          sheetChanged: false,
          sheetIdChanged: false,
          anchorChanged: false,
          contextDirtied: false,
          skipScroll: true,
        });
      },
      rebindColumn(key: ColumnIndex.Key) {
        if (host.core.sheet) CalendarReadRevision.for(host.core.sheet.invalidation).advance();
        this.rebindInvalidatedColumn(key);
      },
      rebindInvalidatedColumn(key: ColumnIndex.Key) {
        // A `rebind.column` frame mark under `frame-marks` (off: one NaN check).
        const mark = FrameMarks.begin();
        host.bindReasons.contentChanged();
        host.core.layout.columns.rebindColumn(key);
        FrameMarks.end("rebind.column", mark, 1, 0);
      },
      rebindTimepoints(range: GenericRange<MsSinceEpoch>) {
        if (host.core.sheet) CalendarReadRevision.for(host.core.sheet.invalidation).advance();
        this.rebindInvalidatedTimepoints(range);
      },
      rebindInvalidatedTimepoints(range: GenericRange<MsSinceEpoch>) {
        // A `rebind.timepoints` frame mark under `frame-marks`
        // (counts: rendered columns searched, columns rebound). A throw leaves
        // it to the enclosing mark to close.
        const mark = FrameMarks.begin();
        let rebound = 0;
        host.bindReasons.contentChanged();
        const sheet = host.core.sheet;
        if (!sheet) return FrameMarks.end("rebind.timepoints", mark, 0, 0);

        const renderedElements = host.core.layout.columns.renderedElements();
        if (renderedElements.length === 0) return FrameMarks.end("rebind.timepoints", mark, 0, 0);

        const renderer = nonnull(sheet).indexRenderer;

        const columnsWithRanges = renderedElements
          .map(({ column }) => {
            const resolved = renderer.mapper.resolveKey(column);
            const defaultResolved = resolved.castToDefault();
            if (!defaultResolved) return null;
            return {
              column,
              range: defaultResolved.range,
              resolved: defaultResolved,
            };
          })
          .filter(Utils.isPresent)
          // TODO: pointless to sort and then binary search. we should
          // maybe try to keep renderedElements sorted
          //
          // Sort by start time - required for binary search algorithm
          .sort((a, b) => a.range.start.mse - b.range.start.mse);

        const sr = ColumnTimepointSearch.findColumnsInTimepointRange(
          columnsWithRanges,
          range,
        );

        if (sr) {
          const startColumn = columnsWithRanges[sr.startIndex];
          const endColumn = columnsWithRanges[sr.endIndex - 1];

          if (host.columnRebindToast?.get() && startColumn !== undefined && endColumn !== undefined) {
            const s = host.navigator?.mapper.resolveKey(startColumn.column);
            const e = host.navigator?.mapper.resolveKey(endColumn.column);

            Toast.info(
              // end uses endColumn (the last column in the span), not startColumn.
              `Rebinding columns: ${startColumn.range.start.date.toString()} to ${endColumn.range.end.date.toString()} (exl)`,
              {
                description: `${sr.endIndex - sr.startIndex} cnt columns, offset=(${s?.offset}..<${e?.offset}), resIndex=(${sr.startIndex}, ${sr.endIndex})`,
                durationMs: 10_000,
              },
            );
          }

          for (const { column } of columnsWithRanges.slice(sr.startIndex, sr.endIndex)) {
            host.core.layout.columns.rebindColumn(column);
            rebound++;
          }
        }

        // Also rebind custom columns (e.g., flight cards) that intersect the range
        for (const { column } of renderedElements) {
          const resolved = renderer.mapper.resolveKey(column);
          const customResolved = resolved.castToCustom();
          if (!customResolved) continue;

          // A custom column with a time range (e.g. FlightInfo's `range`).
          const inner: object = customResolved.inner;
          const customRange = "range" in inner && inner.range instanceof DateTime.Range
            ? inner.range
            : null;
          if (customRange == null) continue;

          const intersects =
            customRange.start.mse < range.end &&
            customRange.end.mse > range.start;

          if (intersects) {
            host.core.layout.columns.rebindColumn(column);
            rebound++;
          }
        }
        FrameMarks.end("rebind.timepoints", mark, renderedElements.length, rebound);
      },
      rebindAll(context: string) {
        if (host.core.sheet) CalendarReadRevision.for(host.core.sheet.invalidation).advance();
        this.rebindInvalidatedAll(context);
      },
      rebindInvalidatedAll(context: string) {
        host.bindReasons.contentChanged();
        if (host.suppressForViewModelSwap(`rebind-all/${context}`)) return;
        host.emitHandle.onBindRow();
        host.root.scrollPort.emitAll(false, `manual-rebind/${context}`);
        // Update span-stacked fixed children after full rebind
        const visibleColumns = host.core.layout.columns.visible;
        if (visibleColumns) {
          host.defaultBinder.updateStaticChildSpans?.(
            nonnull(host.core.sheet, "Sheet: no sheet bound"),
            visibleColumns,
          );
        }
      },
      async rebindAllLegend() {
        if (host.suppressForViewModelSwap("rebind-all-legend")) return;
        if (!host.core.sheet) return;
        await host.bindSheet(host.core.sheet, {
          animate: false,
          rebindLegend: true,
          sheetChanged: false,
          sheetIdChanged: false,
          anchorChanged: false,
          contextDirtied: false,
          skipScroll: true,
        });
      },
      async rebindStructure() {
        // Burst coalescing: structural invalidations arrive in multi-frame
        // bursts (alias re-expansion, then sync/fetch settles — each frame's
        // InvalidationTransaction dispatches at most ONE cell-content-all, but
        // consecutive frames each dispatch their own), and every pass here is
        // a whole-sheet `bindSheet` — a genuinely multi-await pipeline (size
        // gate → legend rebind → layout gate → mutate) that outlives the frame
        // that started it. Each pass reads the CURRENT viewmodel tree, so
        // overlapping passes are pure redundancy: while one is in flight,
        // fold every further request into a single trailing pass that picks
        // up whatever the burst left behind. Trailing-edge semantics keep the
        // final DOM state identical; awaiting callers resolve once the pass
        // covering their request (the in-flight loop) completes. See
        // .requirements/structural-rebind —
        // relocation-does-not-flash-unrelated-cells, driver (3).
        if (host.suppressForViewModelSwap("rebind-structure")) return;
        if (host.state.structuralRebindInFlight) {
          host.state.structuralRebindQueued = true;
          return host.state.structuralRebindInFlight;
        }
        const run = (async () => {
          try {
            do {
              host.state.structuralRebindQueued = false;
              await host.rebindStructurePass();
            } while (host.state.structuralRebindQueued);
          } finally {
            host.state.structuralRebindInFlight = null;
            host.state.structuralRebindQueued = false;
          }
        })();
        host.state.structuralRebindInFlight = run;
        return run;
      },
      async revealRowOptionsPopover(
        row: SheetRow.Joined.Handle,
        target: "default" | "emoji",
      ) {
        const clear = () => {
          host.activeRowOptions.update(null);
        };

        const sheet = host.core.sheet;
        if (!sheet) return clear();

        const variant = liveVariantOf(row);

        // Compose first: a visible row that declares `optionContributions()`
        // gets ONE composed surface through the app-injected renderer, rendered
        // from its OPTION envelopes. Rows without options (or before the app
        // injects a renderer) fall back to their plugin's `makeRowOptions`
        // factory — the legacy shim that stays until every variant is migrated.
        const composedRenderer = host.composedOptionsRenderer;
        const composedEnvelopes = composedRenderer != null && variant != null
          ? RowEnvelopes.optionsFor(variant)
          : null;
        const useComposed = composedEnvelopes != null && composedEnvelopes.length > 0;

        const makeRowOptions = nonnull(host.pluginManager, "Sheet: pluginManager not set").get(row).plugin().makeRowOptions;
        if (!useComposed && !makeRowOptions) return clear();

        if (host.activeRowOptions.get() === row.id && host.state.currentRowOptions) {
          if (host.state.currentRowOptions.bindTarget(target)) return;
        }

        if (host.state.rowOptionsAnchor) {
          host.state.rowOptionsAnchor.dismiss();
        }

        const $anchor = RowOptionsAnchor.make();
        $anchor.style.opacity = "0";
        const aliasInstance =
          variant instanceof AliasRow ? variant.instance() : null;
        const props: RowOptionsElement.Factory.Props = {
          handle: host.handle,
          row,
          dismiss: () => $anchor.dismiss(),
          target,
          standalone: true,
          ...(aliasInstance != null ? { aliasInstance } : {}),
        };
        let $content: Option<RowOptionsElement> = null;
        if (useComposed && composedRenderer != null && composedEnvelopes != null) {
          $content = await composedRenderer({ ...props, envelopes: composedEnvelopes });
        } else if (makeRowOptions) {
          $content = await makeRowOptions(props);
        }
        host.state.currentRowOptions = $content;
        if (!$content) return clear();

        $$.startInstrumentation("row-options", 5);
        host.activeRowOptions.update(row.id);

        $anchor.bind($content, sheet, row, host.legend.offsetWidth, () => {
          DomUtils.removeFromDom($anchor);
          if (host.activeRowOptions.get() === row.id) {
            host.activeRowOptions.update(null);
          }
          if ($anchor != host.state.rowOptionsAnchor) return;
          host.state.rowOptionsAnchor = null;
        });

        host.state.rowOptionsAnchor = $anchor;
        $$.mutate(() => {
          host.core.layout.overlays.attachRowOptionsAnchor($anchor);

          const scrollInfo = nonnull(
            host.root.scrollPort.getScrollInfo(),
            "Sheet: scroll port not measured",
          );

          $anchor.didViewportScroll(
            scrollInfo.top,
            scrollInfo.scrollHeight,
            nonnull(host.size, "Sheet: size not measured").height,
          );

          // we just did a bunch of work to lay it out. we need to wait 1 frame
          // for RowOptionsAnchor's resizeObserver to discover the height
          $$.deferMutate(() => {
            $anchor.style.opacity = "1";
          });
        });
      },
  };
  return handle;
}
